/**
 * `.rl-menu` — the row's overflow actions: the writes that have no column of
 * their own (CAE-1552).
 *
 * On the kit's `Popover` rather than MUI's `Menu`, which is the same choice the
 * info panel beside it makes: `Popover` already dismisses on Escape and on an
 * outside click and moves focus in and back to the trigger, which is CAE-1552's
 * third acceptance criterion, and the two panels on one row behaving differently
 * would be worse than either behaving slightly wrong.
 *
 * > **Two of the handoff's items are not built.** `06-edit-reels/README.md`
 * > lists *Move to Reel N* and *Duplicate*. Neither exists: the API offers
 * > create-from-catalogue, remove, reorder-within-a-reel, asset, enabled and
 * > display-name, and nothing that moves a Reel Option between reels or copies
 * > one. Drawing them would be two controls that do nothing, so they are left
 * > out and raised on CAE-1552 as the API work they are. *Outcome details* is
 * > left out for a different reason — it is the info button immediately to the
 * > left of this one, and a menu item that opens the panel next to it is a
 * > second way to do a thing already one click away.
 * >
 * > What is here instead are the two writes the panel this replaced carried and
 * > the design has no home for: the display-name override — the only place in
 * > the admin that writes `reel_options.display_name_override` — and clearing
 * > an asset, which the image slot alone cannot do.
 */
import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { Theme } from '@mui/material/styles';
import { Popover } from '../../../../components/ui';

const MoreIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
    <circle cx="12" cy="5" r="1.5" fill="currentColor" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    <circle cx="12" cy="19" r="1.5" fill="currentColor" />
  </svg>
);

interface MenuItemProps {
  label: string;
  danger?: boolean;
  onClick: () => void;
}

const MenuItem = ({ label, danger, onClick }: MenuItemProps) => (
  <ButtonBase
    onClick={onClick}
    sx={(theme: Theme) => ({
      display: 'block',
      width: '100%',
      textAlign: 'left',
      padding: '8px 10px',
      borderRadius: `${theme.tokens.radius.badge}px`,
      fontSize: '13px',
      color: danger ? theme.tokens.color.danger : theme.tokens.color.ink2,
      transition: 'background 0.12s',
      '&:hover': {
        backgroundColor: danger
          ? theme.tokens.color.dangerSoft
          : theme.tokens.componentColor.countBadgeSelectedBg,
        color: danger ? theme.tokens.color.danger : theme.tokens.color.ink,
      },
    })}
  >
    {label}
  </ButtonBase>
);

export interface ReelOptionMenuProps {
  /** The Pick Name, for the trigger's accessible name. */
  pickName: string;
  hasAsset: boolean;
  disabled: boolean;
  onEditDisplayName: () => void;
  /** Opens the club and jersey number override (CAE-1610). */
  onEditParticipant: () => void;
  onReplaceAsset: () => void;
  onClearAsset: () => void;
  onRemove: () => void;
}

export const ReelOptionMenu = ({
  pickName,
  hasAsset,
  disabled,
  onEditDisplayName,
  onEditParticipant,
  onReplaceAsset,
  onClearAsset,
  onRemove,
}: ReelOptionMenuProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  // Every item closes the menu first: each one opens a dialog or a confirm, and
  // a menu left standing behind a modal is a panel the operator has to dismiss
  // twice.
  const run = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  return (
    <>
      <Tooltip title="More actions">
        <span>
          <IconButton
            ref={anchorRef}
            size="small"
            disabled={disabled}
            onClick={() => setOpen((previous) => !previous)}
            aria-label={`More actions for ${pickName}`}
            aria-expanded={open}
            sx={(theme: Theme) => ({
              color: open ? theme.tokens.color.accent : theme.tokens.color.ink4,
              backgroundColor: open ? theme.tokens.color.accentSoft : undefined,
            })}
          >
            <MoreIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        label={`Actions for ${pickName}`}
        onClose={() => setOpen(false)}
        width={200}
      >
        <Box sx={{ padding: '5px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <MenuItem label="Edit display name" onClick={run(onEditDisplayName)} />
          <MenuItem label="Edit club & number…" onClick={run(onEditParticipant)} />
          <MenuItem label="Replace asset…" onClick={run(onReplaceAsset)} />
          {hasAsset ? <MenuItem label="Clear asset" onClick={run(onClearAsset)} /> : null}
          <Box
            aria-hidden
            sx={(theme: Theme) => ({
              height: '1px',
              margin: '4px 0',
              backgroundColor: theme.tokens.color.line,
            })}
          />
          <MenuItem label="Remove from reel" danger onClick={run(onRemove)} />
        </Box>
      </Popover>
    </>
  );
};
